import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AudioSettingsProvider } from './contexts/AudioSettingsContext';
import { OAuthCallback } from './components/auth/OAuthCallback';
import { apiClient } from './api/api-client';
import { initializeGPT, enableAndDisplayAds, ClientGameGate } from '@low6-pickem/ui';
import AppEntryView from './views/AppEntryView';
import RevealView from './views/RevealView';
import { getTenantBasePath, isClientGameEnabled, GameEntry } from '@low6-pickem/frontend-shared';
import DebugPickCutoffView from './views/DebugPickCutoffView';
import DebugPpdStatesView from './views/DebugPpdStatesView';
import './App.css';

const TENANT_BASE_PATH = getTenantBasePath();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Health() {
  return (
    <main className="min-h-screen bg-background text-foreground p-6">
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-6">
        <h2 className="text-2xl font-semibold">OK</h2>
        <p className="text-muted-foreground">Routing + React Query + Tailwind are configured.</p>
      </div>
    </main>
  );
}

/**
 * App Content Component
 * Handles authentication check and routing
 *
 * When not authenticated, triggers an API call that returns 401,
 * which the api-client interceptor handles by initiating OAuth login.
 */
function AppContent() {
  const { isLoading, authError } = useAuth();
  const debugPickCutoffEnabled = import.meta.env.VITE_DAILY_REVEAL_DEBUG_PICK_CUTOFF === 'true';
  const debugPpdStatesEnabled =
    import.meta.env.VITE_DAILY_REVEAL_DEBUG_PPD_STATES === 'true' || import.meta.env.DEV;

  const isOAuthCallback = window.location.pathname.endsWith('/auth/callback');

  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH !== 'false' && import.meta.env.DEV;

  useEffect(() => {
    if (isOAuthCallback || isLoading || bypassAuth) return;

    const checkAuth = async () => {
      try {
        await apiClient.get('/api/auth/user-details');
      } catch (_error) {
        // 401 errors are handled by the api-client interceptor (triggers OAuth)
      }
    };

    checkAuth();
  }, [isOAuthCallback, isLoading, bypassAuth]);

  useEffect(() => {
    const initAds = async () => {
      try {
        await initializeGPT();
        enableAndDisplayAds();
      } catch (error) {
        console.error('[App] Failed to initialize GPT:', error);
      }
    };
    initAds();
  }, []);

  if (isOAuthCallback) {
    return (
      <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
        <Routes>
          <Route path="/auth/callback" element={<OAuthCallback />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#53d337] mx-auto mb-4"></div>
          <p className="text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#121212]">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2
            className="text-white text-xl font-bold mb-2"
            style={{ fontFamily: '"Saira Extra Condensed", sans-serif' }}
          >
            Authentication Error
          </h2>
          <p className="text-gray-400 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {authError.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
      <Routes>
        {/* DK iframe entry (DKPLAT-467): /dailydraw?gameId=&tenantId= and
            proxy-stripped /?gameId= both deep-link to /game/:gameId */}
        <Route
          path="/"
          element={
            <GameEntry>
              <AppEntryView />
            </GameEntry>
          }
        />
        <Route
          path="/dailydraw"
          element={
            <GameEntry>
              <AppEntryView />
            </GameEntry>
          }
        />
        <Route path="/game/:gameId" element={<AppEntryView />} />
        <Route path="/reveal" element={<RevealView />} />
        {debugPickCutoffEnabled ? (
          <Route path="/debug/pick-cutoff" element={<DebugPickCutoffView />} />
        ) : null}
        {debugPpdStatesEnabled ? (
          <Route path="/debug/ppd-states" element={<DebugPpdStatesView />} />
        ) : null}
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/health" element={<Health />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default function App() {
  // Layer A: don't register this game's routes if the client didn't deploy it.
  return (
    <ClientGameGate enabled={isClientGameEnabled('dailyReveal')}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AudioSettingsProvider>
            <AppContent />
          </AudioSettingsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ClientGameGate>
  );
}
